import { localDayISO } from './localDay.js'

export const SOTD_HERO_TRACE_LABEL = '[FOS_SOTD_HERO_RENDER]'

function logDay(log) {
  return log?.worn_on || log?.wear_date || ''
}

function isSotd(log) {
  return (log?.wear_label || 'sotd') === 'sotd'
}

function exactCollectionMatch(fragrances, fragranceId) {
  const direct = fragrances.find(fragrance => fragrance.id === fragranceId)
  if (direct) return { fragrance: direct, match: 'exact_collection_id' }

  const group = fragrances.find(fragrance =>
    (fragrance.sizes || []).some(size => size.id === fragranceId)
  )
  if (!group) return null

  // Grouped collection cards use one representative row. The hero still owns
  // the exact ledger id, so keep the display metadata but never substitute the
  // representative id for the id recorded in wear_logs.
  return {
    fragrance: {
      ...group,
      collection_group_id: group.id,
      id: fragranceId,
    },
    match: 'exact_grouped_size_id',
  }
}

function legacySnapshotCandidate(log, fragrances) {
  if (!log?.fragrance_name || !log?.house) return null
  const name = String(log.fragrance_name).toLocaleLowerCase()
  const house = String(log.house).toLocaleLowerCase()
  return fragrances.find(fragrance =>
    String(fragrance.name || '').toLocaleLowerCase() === name
    && String(fragrance.house || '').toLocaleLowerCase() === house
  ) || null
}

/**
 * The Home SOTD decision boundary. A display object is returned only when an
 * owned local-day wear_logs row names its exact fragrance id.
 */
export function selectSotdHero({
  logs = [],
  fragrances = [],
  userId,
  localDay = localDayISO(),
} = {}) {
  const log = logs.find(row =>
    row?.user_id === userId
    && logDay(row) === localDay
    && isSotd(row)
  ) || null

  if (!log) {
    return {
      decision: 'blank',
      reason: 'no_local_day_wear_log',
      log: null,
      fragrance: null,
      blockedSecondSource: null,
    }
  }

  const exact = exactCollectionMatch(fragrances, log.fragrance_id)
  if (!exact) {
    const blocked = legacySnapshotCandidate(log, fragrances)
    return {
      decision: 'blank',
      reason: 'wear_log_fragrance_id_not_in_collection_result',
      log,
      fragrance: null,
      blockedSecondSource: blocked ? {
        fragranceId: blocked.id,
        origin: 'collection_display_query_result.legacy_name_house_fallback',
      } : null,
    }
  }

  return {
    decision: 'render',
    reason: exact.match,
    log,
    fragrance: exact.fragrance,
    blockedSecondSource: null,
  }
}

export function assertSotdHeroLedgerTrace({
  renderedFragranceId,
  logs = [],
  userId,
  localDay = localDayISO(),
} = {}) {
  if (!renderedFragranceId) return true
  const traced = logs.some(log =>
    log?.user_id === userId
    && logDay(log) === localDay
    && isSotd(log)
    && log.fragrance_id === renderedFragranceId
  )
  if (!traced) {
    throw new Error(
      `SOTD_HERO_LEDGER_GUARD: rendered fragrance ${renderedFragranceId} is absent from fragrance.wear_logs for user ${userId || '<missing>'} on ${localDay}`,
    )
  }
  return true
}

export function detectSotdShellSource() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'ssr_test_shell'
  if (window.__FOS_LAST_SW_SHELL_SOURCE__) return window.__FOS_LAST_SW_SHELL_SOURCE__
  if (navigator.serviceWorker?.controller) return 'service_worker_controlled_shell'
  return 'network_shell'
}

export function detectSotdBuildRevision() {
  return typeof __BUILD_REVISION__ === 'undefined' ? 'unbundled_runtime' : __BUILD_REVISION__
}

function browserTraceEnabled() {
  if (typeof window === 'undefined') return false
  if (window.__FOS_SOTD_TRACE__ === true) return true
  try {
    return new URLSearchParams(window.location.search).get('fos_sotd_trace') === '1'
      || window.localStorage.getItem('fos_sotd_trace') === '1'
  } catch {
    return false
  }
}

export function traceSotdHeroRender({
  selection,
  logs = [],
  userId,
  localDay = localDayISO(),
  wearLogSource = 'unknown',
  shellSource = detectSotdShellSource(),
  buildRevision = detectSotdBuildRevision(),
  logger = null,
} = {}) {
  const renderedFragranceId = selection?.fragrance?.id || null
  assertSotdHeroLedgerTrace({ renderedFragranceId, logs, userId, localDay })

  const detail = {
    event: 'FOS_SOTD_HERO_RENDER',
    decision: selection?.decision || 'blank',
    reason: selection?.reason || 'no_selection',
    renderedFragranceId,
    wearLogId: selection?.log?.id || null,
    wearLogFragranceId: selection?.log?.fragrance_id || null,
    userId: userId || null,
    localDay,
    origin: renderedFragranceId
      ? `fragrance.wear_logs.${wearLogSource}`
      : 'none',
    displayMetadataOrigin: renderedFragranceId
      ? 'collection_display_query_result.exact_id'
      : null,
    blockedSecondSource: selection?.blockedSecondSource || null,
    shellSource,
    buildRevision,
  }

  const output = logger || (browserTraceEnabled() ? (...parts) => console.info(...parts) : null)
  output?.(SOTD_HERO_TRACE_LABEL, JSON.stringify(detail))
  return detail
}
